import type { NitroFetchOptions } from "nitropack";
import type { UseApiFetchOptions } from "@edco/apollo/dist/runtime/composables/useApiFetch";
import type { OutcomeTag, TopicTag } from "./useTagResource";
import type { AsyncData } from "#app";

export type UserDetails = {
  name: string;
  id: string;
  nodeId: number;
  autoApproveSubmissions: boolean;
  acceptedGuidelines: boolean;
  userRole: string;
};

export type LoggedInMemberResponseDto = {
  loggedIn: boolean;
  member?: UserDetails;
};

export interface MemberStubDto {
  id: string;
  nodeId: number;
  url: string;
  name: string;
  profilePicture?: MediaStubDto;
  jobTitle?: string;
  orgName?: string;
  orgUrl?: string;
}

export type MediaStubDto = {
  id: string;
  name: string;
  url: string;
};

export type LoginFormData = {
  email: string;
  password: string;
  rememberMe?: boolean;
};

export type RegistrationFormData = {
  interests: string[];
  outcomes: string[];
  userRoles: string[];
  name?: string;
  email?: string;
  password?: string;
};

export type SchoolDto = {
  id: string;
  nodeId: number;
  orgId: string;
  name: string;
  profileImage: MediaStubDto;
  bannerImage: MediaStubDto;
  url: string;
  addressLine1: string;
  addressLine2: string;
  region: string;
  country: string;
};

export type MemberDetails = {
  id: string;
  name: string;
  displayPicture: string;
  bannerImage: { url: string } | null;
  email: string;
  jobTitle: string;
  phoneNumber: string;
  description: string;
  websiteUrl: string;
  linkedInName: string;
  twitterName: string;
  topics: TopicTag[];
  outcomes: OutcomeTag[];
  schools: SchoolDto[];
};

export type ChangePasswordDto = {
  currentPassword: string;
  newPassword: string;
};

export type RequestPasswordResetDto = {
  email: string;
};

export type PasswordResetDto = {
  key: string;
  newPassword: string;
};

export type CloseAccountDto = {
  password: string;
};

export type SaveCoreDetailsDto = {
  name: string;
  email: string;
  jobTitle?: string;
  phoneNumber?: string;
  description?: string;
  websiteUrl?: string;
  twitterName?: string;
  linkedInName?: string;
  topics: string[];
  outcomes: string[];
};

export type OrganizationStub = {
  id: string;
  name: string;
};

export function $isnMemberResource () {
  const dependencies = useFetchDependencies();

  function getLoggedInMember ( abortSignal?: AbortSignal ) {
    const url = "/api/isn/members/me";

    const options: NitroFetchOptions<string> = {
      method: "GET",
      signal: abortSignal,
      headers: {
        Accept: "application/json",
      },
    };

    return $apiFetch<LoggedInMemberResponseDto>( url, options, dependencies );
  }

  function getDetails ( id: string, abortSignal?: AbortSignal ) {
    const url = `/api/isn/members/details/${encodeURIComponent( id )}`;

    const options: NitroFetchOptions<string> = {
      method: "GET",
      signal: abortSignal,
      headers: {
        Accept: "application/json",
      },
    };

    return $apiFetch<MemberDetails>( url, options, dependencies );
  }

  function getOrgs ( abortSignal?: AbortSignal ) {
    const url = "/api/isn/members/me/orgs";

    const options: NitroFetchOptions<string> = {
      method: "GET",
      signal: abortSignal,
      headers: {
        Accept: "application/json",
      },
    };

    return $apiFetch<OrganizationStub[]>( url, options, dependencies );
  }

  function login ( payload: LoginFormData ) {
    const url = "/api/isn/members/login";

    const options: NitroFetchOptions<string> = {
      method: "POST",
      headers: {
        Accept: "application/json",
      },
      body: payload,
    };

    return $apiFetch<void>( url, options, dependencies );
  }

  function logout () {
    const url = "/api/isn/members/logout";

    const options: NitroFetchOptions<string> = {
      method: "POST",
      headers: {
        Accept: "application/json",
      },
    };

    return $apiFetch<void>( url, options, dependencies );
  }

  function register ( payload: RegistrationFormData ) {
    const url = "/api/isn/members/register";

    const options: NitroFetchOptions<string> = {
      method: "POST",
      headers: {
        Accept: "application/json",
      },
      body: payload,
    };

    return $apiFetch<void>( url, options, dependencies );
  }

  function changePassword ( payload: ChangePasswordDto ) {
    const url = "/api/isn/members/me/password";

    const options: NitroFetchOptions<string> = {
      method: "POST",
      headers: {
        Accept: "application/json",
      },
      body: payload,
    };

    return $apiFetch<void>( url, options, dependencies );
  }

  function requestPasswordReset ( payload: RequestPasswordResetDto ) {
    const url = "/api/isn/members/password/reset/request";

    const options: NitroFetchOptions<string> = {
      method: "POST",
      headers: {
        Accept: "application/json",
      },
      body: payload,
    };

    return $apiFetch<void>( url, options, dependencies );
  }

  function resetPassword ( payload: PasswordResetDto ) {
    const url = "/api/isn/members/password/reset";

    const options: NitroFetchOptions<string> = {
      method: "POST",
      headers: {
        Accept: "application/json",
      },
      body: payload,
    };

    return $apiFetch<void>( url, options, dependencies );
  }

  function acceptCommunityGuidelines () {
    const url = "/api/isn/members/me/accept-guidelines";

    const options: NitroFetchOptions<string> = {
      method: "POST",
      headers: {
        Accept: "application/json",
      },
    };

    return $apiFetch<void>( url, options, dependencies );
  }

  function saveCoreDetails ( payload: SaveCoreDetailsDto ) {
    const url = "/api/isn/members/me/details";

    const options: NitroFetchOptions<string> = {
      method: "POST",
      headers: {
        Accept: "application/json",
      },
      body: payload,
    };

    return $apiFetch<void>( url, options, dependencies );
  }

  function saveSchools ( payload: string[] ) {
    const url = "/api/isn/members/me/schools";

    const options: NitroFetchOptions<string> = {
      method: "POST",
      headers: {
        Accept: "application/json",
      },
      body: payload,
    };

    return $apiFetch<void>( url, options, dependencies );
  }

  function closeAccount ( payload: CloseAccountDto ) {
    const url = "/api/isn/members/me/close-account";

    const options: NitroFetchOptions<string> = {
      method: "POST",
      headers: {
        Accept: "application/json",
      },
      body: payload,
    };

    return $apiFetch<void>( url, options, dependencies );
  }

  function removeDisplayPicture () {
    const url = "/api/isn/members/me/avatar/remove";

    const options: NitroFetchOptions<string> = {
      method: "POST",
      headers: {
        Accept: "application/json",
      },
    };

    return $apiFetch<void>( url, options, dependencies );
  }

  function uploadDisplayPicture ( file: FormData ) {
    const url = "/api/isn/members/me/avatar/upload";

    const options: NitroFetchOptions<string> = {
      method: "POST",
      headers: {
        "Content-Type": "multipart/form-data",
      },
      body: file,
    };

    return $apiFetch<string>( url, options, dependencies );
  }

  function removeBannerPicture () {
    const url = "/api/isn/members/me/banner/remove";

    const options: NitroFetchOptions<string> = {
      method: "POST",
      headers: {
        Accept: "application/json",
      },
    };

    return $apiFetch<void>( url, options, dependencies );
  }

  function uploadBannerPicture ( file: FormData ) {
    const url = "/api/isn/members/me/banner/upload";

    const options: NitroFetchOptions<string> = {
      method: "POST",
      headers: {
        "Content-Type": "multipart/form-data",
      },
      body: file,
    };

    return $apiFetch<void>( url, options, dependencies );
  }

  function saveSubscriptionType ( payload: { subscriptionType: string; schoolSize: string | null } ) {
    const url = "/api/isn/members/me/subscription-type";

    const options: NitroFetchOptions<string> = {
      method: "POST",
      headers: {
        Accept: "application/json",
      },
      body: payload,
    };

    return $apiFetch<void>( url, options, dependencies );
  }

  function saveSchoolSubscription ( payload: {
  jobRole: string;
  schoolName: string;
  schoolPostcode: string;
  purchaseOrder: string;
  financeName: string;
  financeEmail: string;
} ) {
    console.log( "Sending school subscription payload:", payload ); // Add this

    const url = "/api/isn/members/me/school-subscription";

    const options: NitroFetchOptions<string> = {
      method: "POST",
      headers: {
        Accept: "application/json",
      },
      body: payload,
    };

    return $apiFetch<void>( url, options, dependencies );
  }

  function saveIndividualSubscription ( payload: {
  jobRole: string;
  schoolName: string;
  schoolPostcode: string;
} ) {
    const url = "/api/isn/members/me/individual-subscription";

    const options: NitroFetchOptions<string> = {
      method: "POST",
      headers: {
        Accept: "application/json",
      },
      body: payload,
    };

    return $apiFetch<void>( url, options, dependencies );
  }

  return {
    getLoggedInMember,
    getDetails,
    getOrgs,
    register,
    login,
    logout,
    changePassword,
    requestPasswordReset,
    resetPassword,
    acceptCommunityGuidelines,
    saveCoreDetails,
    saveSchools,
    closeAccount,
    removeDisplayPicture,
    uploadDisplayPicture,
    removeBannerPicture,
    uploadBannerPicture,
    saveSubscriptionType,
    saveSchoolSubscription,
    saveIndividualSubscription,
  };
}

export function useIsnMemberResource () {
  function getLoggedInMember (): AsyncData<LoggedInMemberResponseDto, Error | null> {
    const url = "/api/isn/members/me";

    const options: UseApiFetchOptions<LoggedInMemberResponseDto> = {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
    };

    return useApiFetch<LoggedInMemberResponseDto>( url, options );
  }

  function getDetails ( id: string ): AsyncData<MemberDetails, Error | null> {
    const url = `/api/isn/members/details/${encodeURIComponent( id )}`;

    const options: UseApiFetchOptions<MemberDetails> = {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
    };

    return useApiFetch<MemberDetails>( url, options );
  }

  function getOrgs (): AsyncData<OrganizationStub[], Error | null> {
    const url = "/api/isn/members/me/orgs";

    const options: UseApiFetchOptions<OrganizationStub[]> = {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
    };

    return useApiFetch<OrganizationStub[]>( url, options );
  }

  return {
    getLoggedInMember,
    getDetails,
    getOrgs,
  };
}
